import { isChatWidgetType, getDefaultChatSettings } from '@/overlay/chatSettings';

export const WIDGET_TYPES = [
	{ value: 'chat-vertical', label: 'Chat (vertical)' },
	{ value: 'chat-horizontal', label: 'Chat (horizontal)' },
	{ value: 'gift', label: 'Alert' },
	{ value: 'gift-goal', label: 'Dono goal' },
	{ value: 'sub-goal', label: 'Membership goal' },
	{ value: 'follower-goal', label: 'Follower goal' },
	{ value: 'viewer-count', label: 'Viewer count' },
	{ value: 'highlighted-message', label: 'Highlighted message' },
	{ value: 'scroller', label: 'Scroller' },
	{ value: 'emote-wall', label: 'Emote wall' },
	{ value: 'ban-reaper', label: 'Ban reaper' },
	{ value: 'word-cloud', label: 'Word cloud' },
	{ value: 'latest-follower', label: 'Latest follower' },
	{ value: 'latest-subscriber', label: 'Latest member' },
	{ value: 'latest-donation', label: 'Latest donation' },
	{ value: 'top-donation', label: 'Top donation' },
];

// Call-only widget types: live camera feeds, not present on plain overlays. Kept in the same catalog as the
// overlay widgets (rather than a separate layer `type`) so the call studio editor can reuse every generic
// layer/widget mechanism (list, toolbar, drag/resize/crop, copy/paste) unchanged.
export const CALL_WIDGET_TYPES = [
	{ value: 'camera', label: 'My camera' },
	{ value: 'speaker', label: 'Speaker' },
	{ value: 'camera-grid', label: 'Dynamic grid' },
];

// For label lookups that should recognize either catalog regardless of which page they're running on.
export const ALL_WIDGET_TYPES = [ ...WIDGET_TYPES, ...CALL_WIDGET_TYPES ];

const CALL_WIDGET_TYPE_VALUES = CALL_WIDGET_TYPES.map(w => w.value);

// image/video and the camera/speaker/dynamic-grid widgets all show a live rectangle of video with its own
// natural aspect ratio, so they get the same treatment: crop, perspective distort and aspect-locked resize.
// A plain overlay widget (chat, alerts, ...) doesn't - the single source of truth for that split, shared by
// OverlayStage.vue, LayerToolbar.vue, LayerPositionForm.vue and every editor's own layer menu.
export function isMediaLayer(layer) {
	if(layer.type === 'image' || layer.type === 'video') return true;
	return layer.type === 'widget' && CALL_WIDGET_TYPE_VALUES.includes(layer.props?.widgetType);
}

// Whether resizing keeps the layer's shape by default. Not the Dynamic grid: its tiles re-flow to whatever shape
// it's given, so there's no shape of its own to preserve.
export function keepsAspectRatio(layer) {
	return isMediaLayer(layer) && layer.props?.widgetType !== 'camera-grid';
}

export const LAYER_COLORS = [ '#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#9ca3af' ];

export const PHYSICS_WIDGET_TYPES = [ 'emote-wall', 'ban-reaper' ];

export const DEFAULT_PROPS = {
	image: () => ({ src: '' }),
	video: () => ({ src: '', muted: true, autoplay: true, loop: true }),
	text: () => ({ text: 'Text', fontFamily: '', fontSize: 64, color: '#ffffff', align: 'left', verticalAlign: 'top', bold: false, italic: false }),
	color: () => ({ fill: '#7c5cff', opacity: 1 }),
	widget: (widgetType) => ({
		uuid: generateUuid(),
		widgetType,
		settings: isChatWidgetType(widgetType) ? getDefaultChatSettings()
			: widgetType === 'speaker' ? { callerId: null, deviceId: null }
			: widgetType === 'camera-grid' ? { excludeHost: false, hideSpeakers: true }
			: {},
		...(PHYSICS_WIDGET_TYPES.includes(widgetType) ? { physics: { collisionMode: 'above', collisionBlacklist: [] } } : {}),
	}),
};

export const DEFAULT_SIZES = {
	image: [ 480, 270 ],
	video: [ 640, 360 ],
	text: [ 600, 120 ],
	color: [ 480, 270 ],
	widget: [ 480, 360 ],
};

// Widgets whose natural shape isn't the generic 480x360 box.
export const WIDGET_DEFAULT_SIZES = {
	'chat-horizontal': [ 1200, 120 ],
	'camera': [ 480, 270 ],
	'speaker': [ 480, 270 ],
	'camera-grid': [ 960, 540 ],
};

export function generateUuid() {
	return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, () => (Math.random() * 15 & 15).toString(16));
}

// Buttons contributed to the above-canvas toolbar by widget types present in the scene.
export const SCENE_TOOLBAR_ACTIONS = {
	'chat-vertical': [ { icon: 'chat-dots', label: 'Send test message', run: api => api.post('alerts/test', { type: 'chat' }) } ],
	'chat-horizontal': [ { icon: 'chat-dots', label: 'Send test message', run: api => api.post('alerts/test', { type: 'chat' }) } ],
	'gift': [ { icon: 'bell', label: 'Test follow alert', run: api => api.post('alerts/test', { type: 'follow' }) } ],
	// Same burst the emote wall's own settings page sends: eight chat messages of four emotes each.
	'emote-wall': [ { icon: 'emoji-smile', label: 'Test emotes', run: api => Promise.all(Array.from({ length: 8 }, () => api.post('alerts/test', { type: 'chat', data: { emotes: 4 } }))) } ],
	'highlighted-message': [ { icon: 'x-circle', label: 'Clear highlighted message', run: api => api.post('selectMessage', null) } ],
};
